import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCallback, setModalCallback] = useState(null);
  const modalCallbackRef = useRef(null);
  const popupRef = useRef(null);
  const pollRef = useRef(null);

  // Keep ref in sync
  useEffect(() => { modalCallbackRef.current = modalCallback; }, [modalCallback]);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleAuthSuccess = useCallback((userData) => {
    setUser(userData);
    setModalOpen(false);
    const cb = modalCallbackRef.current;
    if (cb) {
      cb();
      setModalCallback(null);
      modalCallbackRef.current = null;
    }
    localStorage.removeItem("auth_pending_action");
  }, []);

  const processSessionFromHash = useCallback(async (hash) => {
    const params = new URLSearchParams(hash.replace("#", ""));
    const sessionId = params.get("session_id");
    if (!sessionId) return false;

    try {
      const anonSession = localStorage.getItem("wl_session") || localStorage.getItem("watchlist_session");
      const res = await axios.post(`${API}/auth/session`, {
        session_id: sessionId,
        anon_session_id: anonSession,
      }, { withCredentials: true });
      handleAuthSuccess(res.data);
      return true;
    } catch {
      return false;
    }
  }, [handleAuthSuccess]);

  const login = useCallback(() => {
    if (modalCallbackRef.current) {
      localStorage.setItem("auth_pending_action", "true");
    }

    const redirectUrl = window.location.origin;
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;

    // Try popup approach first
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;

    const popup = window.open(
      authUrl,
      "halal_screener_auth",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=no`
    );

    // If popup was blocked, fallback to redirect
    if (!popup || popup.closed) {
      window.location.href = authUrl;
      return;
    }

    popupRef.current = popup;

    // Poll the popup for the redirect with session_id
    pollRef.current = setInterval(async () => {
      try {
        if (popup.closed) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          return;
        }
        const popupUrl = popup.location.href;
        if (popupUrl && popupUrl.includes("#session_id=")) {
          const hash = popup.location.hash;
          popup.close();
          clearInterval(pollRef.current);
          pollRef.current = null;
          await processSessionFromHash(hash);
        }
      } catch {
        // Cross-origin error is expected while on auth.emergentagent.com
      }
    }, 500);
  }, [processSessionFromHash]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* */ }
    setUser(null);
  };

  const showAuthModal = (callback) => {
    if (user) {
      if (callback) callback();
      return;
    }
    setModalCallback(() => callback);
    setModalOpen(true);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, showAuthModal,
      modalOpen, setModalOpen, modalCallback,
      setUser, handleAuthSuccess, processSessionFromHash,
      isPremium: user?.premium === true,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
